/* global angular, Modernizr */
/* eslint-disable no-invalid-this */

var _ = require('lodash');

var DEFAULT_OPTIONS = {
  stepSize: 10,
  min: 10,
  max: 100,
  discrete: true
};

/** HELPER FUNCTIONS **/

/**
 * Used to only update opacity on enter or up/down arrow keys in input
 * @param   {object} e input event
 * @returns {Boolean}  true if Enter or arrow key is pressed and source element is input
 */
function updateOnInputKeyEvent (e) {
  // In order: Enter, Up, Down
  var acceptableKeyCodes = [13, 38, 40];

  return acceptableKeyCodes.indexOf(e.keyCode) > -1 && e.srcElement.tagName === 'INPUT';
}

/**
 * Used to only update opacity on left/right arrow keys on slider
 * @param   {object} e input event
 * @returns {Boolean}  true if arrow key is pressed and source element is slider
 */
function updateOnSliderKeyEvent (e) {
  var acceptableKeyCodes = [37, 39];

  return acceptableKeyCodes.indexOf(e.keyCode) > -1 && e.srcElement.tagName === 'SB-SLIDER';
}

/**
 * Strips the protocol from the url and parses it into pieces
 * @param   {string} url layer url
 * @returns {array}      array of url pieces split on / character
 */
function parseUrl (url) {
  return url.replace(/.*?:\/\//g, '').split('/');
}

/**
 * determine if a layer is a tile layer and not a vector layer
 * @param   {object}  layer the leaflet layer
 * @returns {string}  the layer's url or undefined if not a tile layer
 */
function isTileLayer (layer) {
  return layer._url;
}

/**
 * determine if a tile layer is an overlay
 * @param   {object}  layer the leaflet layer
 * @returns {Boolean}       true if the parsed url contains the generic parameter
 */
function isOverlay (layer) {
  var parsedUrl = parseUrl(layer._url);
  return parsedUrl.indexOf('generic') > -1;
}

/**
 * determine if a tile layer is a datacapture layer and not the sat map layer
 * @param   {object}  layer the leaflet layer
 * @returns {Boolean}       true if the parsed url contains the data_captures parameter
 */
function isDatacapture (layer) {
  var parsedUrl = parseUrl(layer._url);

  return parsedUrl.indexOf('data_captures') > -1;
}

/**
 * to improve visibility, we want to scale the sat map's opacity down from the datacapture's opacity
 * @param   {object} layer the leaflet layer
 * @param   {number} opacity value from slider, already converted to value between 0 and 1
 * @returns {void}
 */
function scaleSatMapOpacity (layer, opacity) {
  if (opacity >= 0.9) {
    layer.setOpacity(opacity - 0.2);
  } else {
    layer.setOpacity(opacity - 0.3);
  }
}

/**
 * Constructor function
 * @param {obj} $scope  controller scope
 * @param {obj} map     leaflet map
 * @param {obj} options user-defined options
 * @returns {void}
 */
function OpacitySliderRenderer ($scope, map, options) {
  this._scope = $scope;
  this._map = map;
  this._options = _.assign(DEFAULT_OPTIONS, options);
  this._createElement();
  this._registerListeners();
}

OpacitySliderRenderer.prototype = {

  _buildSlider: function () {
    var discrete = this._options.discrete ? 'sb-discrete' : '';
    var step = this._options.discrete ? ('step="' + this._options.stepSize + '"') : '';
    var min = 'min="' + this._options.min + '"';
    var max = 'max="' + this._options.max + '"';

    var slider = [
      '<sb-slider',
      discrete,
      step,
      min,
      max,
      'ng-model="vm._sliderValue"></div>'
    ].join(' ');

    return slider;
  },

  _buildInput: function () {
    var step = this._options.discrete ? ('step="' + this._options.stepSize + '"') : '';
    var min = 'min="' + this._options.min + '"';
    var max = 'max="' + this._options.max + '"';

    var input = [
      '<input',
      'type="number"',
      'class="opacity-display"',
      step,
      min,
      max,
      'ng-model="vm._opacity" />'
    ].join(' ');

    return input;
  },

  _buildTemplate: function () {
    var slider = this._buildSlider();
    var input = this._buildInput();

    /* eslint-disable */
    var tpl = [
      '<div class="opacity-slider">',
        '<div class="slider-container">',
          slider,
        '</div>',
        input,
      '</div>'
    ].join('');
    /* eslint-enable */

    return tpl;
  },

  // Should be called when the $scope is destroyed
  removeListeners: function () {
    var update = this._update.bind(this);

    this._element.off('touchmove mousemove mouseup touchend keyup click', update);
  },

  getElement: function () {
    return this._element;
  },

  _createElement: function () {
    var tpl = this._buildTemplate();

    this._element = angular.element(tpl);
    this._input = angular.element(this._element[1]);
  },

  _registerListeners: function () {
    var update = this._update.bind(this);

    this._element.on('touchmove mousemove mouseup touchend keyup click', update);
  },

  _setOpacity: function (val) {
    // convert to value between 0 and 1
    var opacity = val / 100;

    this._map.eachLayer(function (layer) {
      if (isTileLayer(layer)) {
        if (isDatacapture(layer)) {
          layer.setOpacity(opacity);
        } else if (!isOverlay(layer)) {
          if (opacity === 1) {
            layer.setOpacity(opacity);
          } else {
            scaleSatMapOpacity(layer, opacity);
          }
        }
      }
    });
  },

  _updateFromInput: function () {
    var val = parseInt(this._input.val(), 10);
    this._scope.$apply(function () {

      if (val < 10) {
        val = 10;
        this._scope.vm._opacity = val;
      } else if (val > 100) {
        val = 100;
        this._scope.vm._opacity = val;
      }

      this._scope.vm._sliderValue = val;
    }.bind(this));

    this._setOpacity(val);
  },

  _updateFromSlider: function () {
    var val = this._scope.vm._sliderValue;
    this._scope.$apply(function () {
      this._scope.vm._opacity = val;
    }.bind(this));

    this._setOpacity(val);
  },

  _update: function (e) {
    if (updateOnInputKeyEvent(e)) {
      this._updateFromInput();
    }

    if (updateOnSliderKeyEvent(e) || (e.type === 'touchmove' || e.type === 'mousemove' || e.type === 'mouseup' || e.type === 'touchend' || e.type === 'click') && e.srcElement.tagName === 'DIV') {
      this._updateFromSlider();
    }
  }
};

module.exports = OpacitySliderRenderer;



// WEBPACK FOOTER //
// ./~/@skycatch/skydash-saturn/boosters/map/src/app/controls/lib/OpacitySliderRenderer.js